import type {UGCMediaCompletedSerializer} from "@/generated/CampaignApplication";
import {useGenericInfiniteQuery, useGenericQuery} from "@/hooks/useQueryHooks";
import {useSelectedCompanyId} from "@/hooks/useUserHooks";
import i18n from "@/i18n";
import type {
  TCampaignType,
  TInfiniteQueryOptions,
  TInfluencerContent,
  TInfluencerProfileDetail,
  TPaginated,
  TQueryOptions,
  TQueryResultWithCount,
} from "@/store/types";
import {useInfiniteQuery} from "@tanstack/react-query";
import type {AxiosError, AxiosResponse} from "axios";
import {uniqBy} from "lodash";

export const INFLUENCER_KEYS = {
  mInfluencerInstagramFeed: (influencerId: number, mediaId: string) => [
    `/influencer/${influencerId}/instagram_feed/${mediaId}/`,
  ],
  qInfluencer: (influencerId: number, params?: string) => [`/influencer/${influencerId}/?${params}`],
  qInfluencerActiveCampaigns: (influencerId: number) => [`/influencer/${influencerId}/active_campaigns/`],
  qInfluencerAwards: (influencerId: number) => [`/influencer/${influencerId}/awards/`],
  qInfluencerCampaignsWorkedWith: (influencerId: number, params?: string) => [
    `/influencer/${influencerId}/campaigns_worked_with/?${params}`,
  ],
  qInfluencerCompletedContent: (influencerId: number, params = "") => [
    `/influencer/${influencerId}/completed_content/${params ? `?${params}` : ""}`,
  ],
  qInfluencerCompletedUgcApplications: (influencerId: number) => [
    `/influencer/${influencerId}/completed_ugc_applications/`,
  ],
  qInfluencerInstagramFeed: (params?: string) => [`/influencer/instagram_feed/?${params}`],
  qInfluencerList: (params?: string | null) => [`/influencer/?${params}`],
  qInfluencerNetworkData: (params?: string) => [`/influencer/network_data/?${params}`],
  qInfluencerTikTokFeed: (params?: string) => [`/influencer/tiktok_feed/?${params}`],
  qSimilarInfluencers: (influencerId: number, params?: string) => [
    `/influencer/${influencerId}/similar_influencers/?${params}`,
  ],
  qSimilarInfluencersNewGroup: (influencerId: number, params?: string) => [
    `/influencer/${influencerId}/similar_influencers_new_group/?${params}`,
  ],
} as const;

export const useInfluencerQuery = (params: {
  id?: number;
  sourceParams?: string;
  queryOptions?: TQueryOptions<TInfluencerProfileDetail>;
}) => {
  const {id, sourceParams, queryOptions} = params;
  const companyId = useSelectedCompanyId();

  return useGenericQuery<TInfluencerProfileDetail>({
    queryKey: INFLUENCER_KEYS.qInfluencer(
      id!,
      `${sourceParams ?? ""}${!!sourceParams?.length ? "&" : ""}${!!companyId ? "company_id=" + companyId : ""}`,
    ),
    enabled: !!id,
    ...queryOptions,
  });
};

export const useInfluencerListQuery = ({
  queryOptions,
  searchParams,
}: {
  queryOptions?: TInfiniteQueryOptions<TInfluencerProfileDetail>;
  searchParams?: string | null;
}) =>
  useGenericInfiniteQuery<TInfluencerProfileDetail>({
    queryKey: INFLUENCER_KEYS.qInfluencerList(searchParams),
    select: response =>
      uniqBy(
        response.pages.flatMap(page => page.data.results),
        "uuid",
      ),
    ...queryOptions,
  });

export const useInfluencerContentQuery = ({
  influencerId,
  campaignType,
}: {
  influencerId: number;
  campaignType?: TCampaignType;
}) => {
  const queryKey =
    campaignType === "ugc"
      ? INFLUENCER_KEYS.qInfluencerCompletedUgcApplications(influencerId)
      : INFLUENCER_KEYS.qInfluencerCompletedContent(influencerId, campaignType ? `${campaignType}=true` : "");

  return useInfiniteQuery<
    AxiosResponse<
      TPaginated<TInfluencerContent> & {
        ugc_application_video?: UGCMediaCompletedSerializer;
      }
    >,
    AxiosError,
    TQueryResultWithCount<TInfluencerContent[]>
  >({
    queryKey,
    initialPageParam: queryKey[0],
    getNextPageParam: lastPage => lastPage.data.next,
    enabled: !!influencerId,
    select: response => {
      const allResults = response.pages.flatMap(page => page.data.results);
      const ugcApplicationVideo = response.pages[0].data.ugc_application_video;

      if (ugcApplicationVideo) {
        allResults.unshift({
          ugc_medias: [ugcApplicationVideo],
          isUgcApplicationContent: true,
          campaign: {
            campaign_type: "UGC",
            title: i18n.t("influencer_profile:ugc_application"),
            company_profile: {company_name: "Refluenced"},
          },
        } as TInfluencerContent);
      }

      return {
        count: response.pages[0].data.count + (!!ugcApplicationVideo ? 1 : 0),
        results: allResults,
      };
    },
  });
};
